import React, { useState } from 'react'
import { Button, Icon } from 'semantic-ui-react'
import styled from 'styled-components'

import PageHeader from '../../components/pageHeader/pageHeader'
import PagedTable from '../../components/dataTable/pagedTable'
import RemoteValue from '../../components/remoteValues/remoteValue'

import { Candidate, Company, Device } from '../../types'
import DeviceSidePanel from './DeviceSidePanel'

const PageContent = styled.div`
  margin: 0px 50px 50px 50px;
`

const Devices = () => {
  document.title = 'Devices'

  const [panelState, setPanelState] = useState<{
    open: boolean
    deviceId?: string | null
  }>({ open: false, deviceId: null })

  const [tableRefreshKey, setTableRefreshKey] = useState(0)

  const handleAddDevice = () => {
    setPanelState({ open: true, deviceId: null })
  }

  const handleEditDevice = (device: Device) => {
    setPanelState({ open: true, deviceId: device._id })
  }

  const handleClosePanel = () => {
    setPanelState({ open: false, deviceId: null })
  }

  const handleReloadTable = () => {
    setTableRefreshKey((prev) => prev + 1)
  }

  const handleSaved = () => {
    handleReloadTable()
    handleClosePanel()
  }

  const handleDeleted = () => {
    handleReloadTable()
    handleClosePanel()
  }

  return (
    <React.Fragment>
      <PageHeader
        breadcrumb={[
          { text: 'Dashboard', link: '/' },
          { text: 'Devices', link: '/devices' },
        ]}
        title="Devices"
        sub="Keep track of all company hardware."
        actions={
          <Button basic onClick={handleAddDevice}>
            <Icon name="add" /> Add Device
          </Button>
        }
      />

      <PageContent>
        <PagedTable<Device>
          key={tableRefreshKey}
          collection="devices"
          defaultSort="lastUpdateOn"
          onRowClick={handleEditDevice}
          columns={[
            {
              title: 'Type',
              field: 'type',
              width: 140,
            },
            {
              title: 'Serial Number',
              field: 'serialNumber',
              width: 180,
            },

            {
              title: 'Brand',
              field: 'brand',
              width: 150,
            },
            {
              title: 'Model',
              field: 'model',
              width: 160,
            },

            {
              title: 'Owner',
              field: 'companyId',
              width: 190,
              render: (companyId: string | null | undefined) =>
                companyId ? (
                  <RemoteValue
                    collection="companies"
                    id={companyId}
                    predicate={(company: Company) => company.name || '—'}
                    showLoading
                    key={companyId}
                  />
                ) : (
                  '—'
                ),
            },

            {
              title: 'Current Location',
              field: 'currentLocation',
              width: 170,
            },

            {
              title: 'Assigned To',
              field: 'candidateId',
              width: 200,
              render: (candidateId: string | null | undefined) =>
                candidateId ? (
                  <RemoteValue
                    collection="candidates"
                    id={candidateId}
                    predicate={(candidate: Candidate) => candidate.name || '—'}
                    showLoading
                    key={candidateId}
                  />
                ) : (
                  '—'
                ),
            },

            {
              title: 'Status',
              field: 'status',
              width: 140,
              align: 'center',
            },
          ]}
        />

        <DeviceSidePanel
          open={panelState.open}
          deviceId={panelState.deviceId ?? undefined}
          onClose={handleClosePanel}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      </PageContent>
    </React.Fragment>
  )
}

export default Devices
